import { useState, useEffect } from 'react';
import { Plus, Search, Pencil, Trash2, Fuel, Car, User, DollarSign } from 'lucide-react';
import { getVehicles, createVehicle, updateVehicle, deleteVehicle, getVehicleLogs, addFuelLog, getFuelPrices, saveFuelPrice } from '../api/vehicles';
import { getFieldStaff } from '../api/users';
import { Button } from '../components/ui/Button';
import { Input, Select, Textarea } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';

const FUEL_TYPES = [
  { value: 'benzin', label: 'Benzin' },
  { value: 'dizel', label: 'Dizel' },
  { value: 'elektrik', label: 'Elektrik' },
  { value: 'hybrid', label: 'Hybrid' },
];
const FUEL_LABEL = { benzin: 'Benzin', dizel: 'Dizel', elektrik: 'Elektrik', hybrid: 'Hybrid' };
const FUEL_UNIT = { benzin: 'L/100km', dizel: 'L/100km', elektrik: 'kWh/100km', hybrid: 'L/100km' };

// ─── Araç Formu ─────────────────────
function VehicleForm({ vehicle, staffList, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    plate:            vehicle?.plate || '',
    brand:            vehicle?.brand || '',
    model:            vehicle?.model || '',
    year:             vehicle?.year  || '',
    fuel_type:        vehicle?.fuel_type || 'benzin',
    fuel_consumption: vehicle?.fuel_consumption || '',
    assigned_to:      vehicle?.assigned_to || '',
    notes:            vehicle?.notes || '',
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.plate.trim() || !form.brand.trim()) { setError('Plaka ve marka zorunlu'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year) : undefined,
        fuel_consumption: form.fuel_consumption ? parseFloat(form.fuel_consumption) : undefined,
        assigned_to: form.assigned_to || undefined,
      };
      vehicle ? await updateVehicle(vehicle.id, payload) : await createVehicle(payload);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Hata oluştu');
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input label="Plaka *" value={form.plate} onChange={set('plate')} placeholder="34 ABC 123" />
        <Input label="Marka *" value={form.brand} onChange={set('brand')} placeholder="Toyota" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Input label="Model" value={form.model} onChange={set('model')} placeholder="Corolla" />
        <Input label="Yıl" type="number" value={form.year} onChange={set('year')} placeholder="2024" />
        <Select label="Yakıt Tipi" value={form.fuel_type} onChange={set('fuel_type')}>
          {FUEL_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input label={`Yakıt Tüketimi (${FUEL_UNIT[form.fuel_type]})`} type="number" step="0.1"
          value={form.fuel_consumption} onChange={set('fuel_consumption')} placeholder="7.5" />
        <Select label="Zimmetli Personel" value={form.assigned_to} onChange={set('assigned_to')}>
          <option value="">— Atanmamış —</option>
          {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
      </div>
      <Textarea label="Notlar" value={form.notes} onChange={set('notes')} rows={2} placeholder="Ek bilgi..." />
      {error && <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" loading={loading}>{vehicle ? 'Güncelle' : 'Kaydet'}</Button>
      </div>
    </form>
  );
}

// ─── Yakıt Log Formu ─────────────────
function FuelLogForm({ vehicleId, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), liters: '', cost_per_liter: '', total_cost: '', km_reading: '', notes: '' });

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm(f => {
      const next = { ...f, [k]: val };
      if ((k === 'liters' || k === 'cost_per_liter') && next.liters && next.cost_per_liter) {
        next.total_cost = (parseFloat(next.liters) * parseFloat(next.cost_per_liter)).toFixed(2);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.liters || !form.cost_per_liter) { setError('Litre ve birim fiyat zorunlu'); return; }
    setLoading(true);
    try {
      await addFuelLog(vehicleId, {
        date: form.date, liters: parseFloat(form.liters),
        cost_per_liter: parseFloat(form.cost_per_liter),
        total_cost: parseFloat(form.total_cost),
        km_reading: form.km_reading ? parseInt(form.km_reading) : undefined,
        notes: form.notes || undefined,
      });
      onSuccess();
    } catch (err) { setError(err.response?.data?.error || 'Hata'); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Tarih" type="date" value={form.date} onChange={set('date')} />
      <div className="grid grid-cols-3 gap-4">
        <Input label="Litre *" type="number" step="0.01" value={form.liters} onChange={set('liters')} />
        <Input label="Birim Fiyat (TL) *" type="number" step="0.01" value={form.cost_per_liter} onChange={set('cost_per_liter')} />
        <Input label="Toplam (TL)" type="number" step="0.01" value={form.total_cost} onChange={set('total_cost')} disabled />
      </div>
      <Input label="KM Sayacı" type="number" value={form.km_reading} onChange={set('km_reading')} placeholder="Opsiyonel" />
      <Textarea label="Not" value={form.notes} onChange={set('notes')} rows={2} />
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" loading={loading}>Kaydet</Button>
      </div>
    </form>
  );
}

// ─── Yakıt Fiyatları Bölümü ──────────
function FuelPricesSection() {
  const [prices, setPrices] = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState('');

  useEffect(() => {
    getFuelPrices().then(d => setPrices(d.prices)).catch(() => {});
  }, []);

  const handleSave = async (fuelType) => {
    const val = parseFloat(editing[fuelType]);
    if (!val || val <= 0) return;
    setSaving(fuelType);
    try {
      await saveFuelPrice({ fuel_type: fuelType, price: val });
      setPrices(p => ({ ...p, [fuelType]: { ...p[fuelType], price: val, fuel_type: fuelType } }));
      setEditing(e => ({ ...e, [fuelType]: undefined }));
    } catch { }
    setSaving('');
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2"><DollarSign size={16} /> Güncel Yakıt Fiyatları</h3>
      <div className="grid grid-cols-4 gap-4">
        {FUEL_TYPES.map(({ value: ft, label }) => {
          const current = prices[ft];
          const unit = ft === 'elektrik' ? 'TL/kWh' : 'TL/L';
          return (
            <div key={ft} className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">{label} ({unit})</p>
              <div className="flex items-center gap-2">
                <input
                  type="number" step="0.01"
                  value={editing[ft] !== undefined ? editing[ft] : (current?.price ?? '')}
                  onChange={e => setEditing(ed => ({ ...ed, [ft]: e.target.value }))}
                  placeholder="0.00"
                  className="w-24 border border-slate-300 rounded px-2 py-1 text-sm"
                />
                {editing[ft] !== undefined && (
                  <Button size="sm" onClick={() => handleSave(ft)} loading={saving === ft}>Kaydet</Button>
                )}
              </div>
              {current?.created_at && (
                <p className="text-[10px] text-slate-400 mt-1">
                  Son: {new Date(current.created_at).toLocaleDateString('tr-TR')}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Yakıt Log Detay ────────────────
function FuelLogsModal({ vehicle, onClose }) {
  const [logs, setLogs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    getVehicleLogs(vehicle.id).then(d => setLogs(d.logs)).catch(() => {});
  };
  useEffect(load, [vehicle.id]);

  return (
    <Modal open onClose={onClose} title={`${vehicle.plate} — Yakıt Logları`} size="lg">
      <div className="mb-4 flex justify-end">
        <Button size="sm" onClick={() => setShowAdd(true)}><Plus size={14} /> Yakıt Ekle</Button>
      </div>

      {showAdd && (
        <div className="mb-4 border rounded-lg p-4 bg-slate-50">
          <FuelLogForm vehicleId={vehicle.id} onSuccess={() => { setShowAdd(false); load(); }} onCancel={() => setShowAdd(false)} />
        </div>
      )}

      {logs.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">Henüz yakıt kaydı yok</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-slate-500">
              <th className="pb-2 font-medium">Tarih</th>
              <th className="pb-2 font-medium text-right">Litre</th>
              <th className="pb-2 font-medium text-right">Birim (TL)</th>
              <th className="pb-2 font-medium text-right">Toplam (TL)</th>
              <th className="pb-2 font-medium text-right">KM</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id} className="border-b last:border-0">
                <td className="py-2">{new Date(l.date).toLocaleDateString('tr-TR')}</td>
                <td className="py-2 text-right">{parseFloat(l.liters).toFixed(1)}</td>
                <td className="py-2 text-right">{parseFloat(l.cost_per_liter).toFixed(2)}</td>
                <td className="py-2 text-right font-medium">{parseFloat(l.total_cost).toFixed(2)}</td>
                <td className="py-2 text-right text-slate-500">{l.km_reading ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// ─── ANA SAYFA ──────────────────────
export function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [logsVehicle, setLogsVehicle] = useState(null);

  const load = () => {
    setLoading(true);
    getVehicles().then(d => setVehicles(d.vehicles)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    getFieldStaff().then(d => setStaffList(d.users)).catch(() => {});
  }, []);

  const handleDelete = async (v) => {
    if (!window.confirm(`${v.plate} aracını silmek istediğinizden emin misiniz?`)) return;
    try { await deleteVehicle(v.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Silinemedi'); }
  };

  const filtered = vehicles.filter(v => {
    if (!search) return true;
    const q = search.toLowerCase();
    return v.plate.toLowerCase().includes(q) || v.brand.toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q) || (v.assigned_to_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Araçlar</h1>
          <p className="text-slate-500 text-sm mt-0.5">{vehicles.length} araç kayıtlı</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}><Plus size={16} /> Yeni Araç</Button>
      </div>

      {/* Yakıt Fiyatları */}
      <FuelPricesSection />

      {/* Arama */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Plaka, marka veya personel ara..."
          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm" />
      </div>

      {/* Tablo */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Car size={40} className="mx-auto mb-3 opacity-40" />
          <p>Araç bulunamadı</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-slate-500 border-b">
                <th className="px-4 py-3 font-medium">Plaka</th>
                <th className="px-4 py-3 font-medium">Marka / Model</th>
                <th className="px-4 py-3 font-medium">Yakıt</th>
                <th className="px-4 py-3 font-medium">Tüketim</th>
                <th className="px-4 py-3 font-medium">Zimmetli</th>
                <th className="px-4 py-3 font-medium">Durum</th>
                <th className="px-4 py-3 font-medium text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{v.plate}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {v.brand} {v.model}{v.year ? ` (${v.year})` : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                      <Fuel size={11} /> {FUEL_LABEL[v.fuel_type]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">
                    {v.fuel_consumption ? `${v.fuel_consumption} ${FUEL_UNIT[v.fuel_type]}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {v.assigned_to_name ? (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                        <User size={11} /> {v.assigned_to_name}
                      </span>
                    ) : <span className="text-slate-400 text-xs">Atanmamış</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${v.is_active ? 'text-green-600' : 'text-slate-400'}`}>
                      {v.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setLogsVehicle(v)} title="Yakıt Logları">
                        <Fuel size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditVehicle(v)}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(v)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Yeni Araç" size="lg">
        <VehicleForm staffList={staffList} onSuccess={() => { setCreateOpen(false); load(); }} onCancel={() => setCreateOpen(false)} />
      </Modal>

      <Modal open={!!editVehicle} onClose={() => setEditVehicle(null)} title="Aracı Düzenle" size="lg">
        {editVehicle && (
          <VehicleForm vehicle={editVehicle} staffList={staffList} onSuccess={() => { setEditVehicle(null); load(); }} onCancel={() => setEditVehicle(null)} />
        )}
      </Modal>

      {logsVehicle && <FuelLogsModal vehicle={logsVehicle} onClose={() => setLogsVehicle(null)} />}
    </div>
  );
}
